import { APIModel } from "../../api-model";
import { AssociatedUser } from "../../associated-user";
import { AppliedTemplate } from "./applied-template";
import { Item } from "./item";

export interface StatusHistoryEntry {
  status: string;
  changed_at: string;
  changed_by: string | null;
}

export class AppliedItem extends APIModel {
  id: string;
  applied_template_id: string;
  template_item: Item;
  item_body_instance: string;
  status: string;
  signed_by: AssociatedUser;
  signed_at: string;
  created_by_id: string;
  created_by: AssociatedUser;
  updated_by_id: string;
  updated_by: AssociatedUser;
  status_changed_at: string;
  status_history: Array<StatusHistoryEntry>;
  applied_template: AppliedTemplate;

  original_state: {
    status: string;
    item_body_instance: string;
  }

  static fieldsToMap = {
    direct: [
      'id',
      'checklist_template_id',
      'applied_template_id',
      'created_by_id',
      'updated_by_id',
      'status',
      'item_body_instance',
      'signed_at',
      'status_changed_at'
    ]
  }

  static buildFromAPI(record: any, appliedTemplate: AppliedTemplate): AppliedItem {
    const row = new AppliedItem();
    record = record || {};

    this.mapFieldsDirectly<AppliedItem>(AppliedItem.fieldsToMap.direct, record, row);

    row.template_item = Item.buildFromAPI(record.template_item);
    row.signed_by = AssociatedUser.buildFromAPI(record.signed_by);
    row.created_by = AssociatedUser.buildFromAPI(record.created_by);
    row.updated_by = AssociatedUser.buildFromAPI(record.updated_by);
    row.status_history = record.status_history || [];
    row.applied_template = appliedTemplate;

    row.captureOriginalState();

    return row;
  }

  // Key order drives the status dropdown display order.
  static STATUS_LABELS: Record<string, string> = {
    done: 'Done',
    na: 'N/A',
    in_progress: 'In Progress',
    cancelled: 'Cancelled',
    not_reviewed: 'Not Reviewed'
  }

  static STATUS_BADGE_CLASSES: Record<string, string> = {
    not_reviewed: 'badge-na',
    na: 'badge-na',
    in_progress: 'badge-pending',
    cancelled: 'badge-failed',
    done: 'badge-complete'
  }

  static translateStatuses(statuses: Array<any>) {
    const byCode = new Map(statuses.map(s => [s.code, s]));
    return Object.keys(this.STATUS_LABELS)
      .filter(code => byCode.has(code))
      .map(code => ({ ...byCode.get(code), label: this.STATUS_LABELS[code] }));
  }

  get isDirty(): boolean {
    return JSON.stringify(this.original_state) !== JSON.stringify(this.buildState());
  }

  get rowClass(): string {
    return this.isDirty ? 'row-edited' : '';
  }

  captureOriginalState() {
    this.original_state = this.buildState();
  }

  markClean() {
    this.captureOriginalState();
  }

  resetChanges() {
    this.status = this.original_state.status;
    this.item_body_instance = this.original_state.item_body_instance;
  }

  private buildState() {
    return {
      status: this.status,
      item_body_instance: this.item_body_instance
    };
  }
}